import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getNotificationById, markNotificationAsRead, AppNotification } from '../services/notificationService';
import { Flame, Loader2, Share2, Swords, MessageSquare, ArrowLeft } from 'lucide-react';
import { getAvatarUrl } from '../utils/avatarUtils';
import html2canvas from 'html2canvas';

export default function ProvocationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notification, setNotification] = useState<AppNotification | null>(null);
  const [loading, setLoading] = useState(true);
  const shareRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchNotification = async () => {
      if (!currentUser || !id) return;
      
      try {
        const notif = await getNotificationById(currentUser.uid, id);
        if (notif) {
          setNotification(notif);
          if (!notif.read) {
            await markNotificationAsRead(currentUser.uid, id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch notification", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNotification();
  }, [currentUser, id]);

  const handleShare = async () => {
    if (!shareRef.current || !notification) return;
    
    try {
      const canvas = await html2canvas(shareRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null
      });
      
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], 'provocation.png', { type: 'image/png' });
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              title: 'I was challenged!',
              text: `${notification.fromUserName} provoked me on LinguaLearn! Watch me beat them. 🔥`,
              files: [file]
            });
          } catch (err) {
            console.error('Error sharing', err);
          }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'provocation.png';
          a.click();
          URL.revokeObjectURL(url);
        }
      });
    } catch (error) {
      console.error('Error generating image', error);
    }
  };

  const handleChallengeBack = () => {
    if (notification?.fromUserId) {
      navigate('/social', { state: { challengeUserId: notification.fromUserId, activeTab: 'challenges' } });
    }
  };

  const handleProvokeBack = () => {
    if (notification?.fromUserId) {
      navigate('/social', { state: { provokeUserId: notification.fromUserId } });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!notification) {
    return (
      <div className="max-w-4xl mx-auto p-4 md:p-8 flex flex-col items-center justify-center min-h-[60vh]">
        <Flame className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold text-gray-700">Provocation Not Found</h2>
        <p className="text-gray-500 mt-2">This provocation may have been deleted or doesn't exist.</p>
        <button 
          onClick={() => navigate('/dashboard')}
          className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 pb-20 md:pb-8">
      <button 
        onClick={() => navigate('/dashboard')}
        className="flex items-center text-gray-500 hover:text-gray-700 transition-colors mb-6"
      >
        <ArrowLeft className="w-5 h-5 mr-1" /> Back to Dashboard
      </button>

      <div className="bg-white rounded-3xl shadow-xl border border-rose-100 overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-r from-rose-500 to-red-600"></div>
        
        <div className="relative pt-16 px-6 pb-8 md:px-12 md:pb-12 text-center">
          <div className="relative inline-block">
            <div className="w-32 h-32 mx-auto rounded-full border-4 border-white shadow-xl overflow-hidden bg-white mb-6">
              <img referrerPolicy="no-referrer" 
                src={notification.fromUserPhoto || getAvatarUrl(notification.fromUserName || notification.fromUserId || 'User')} 
                alt={notification.fromUserName}
                className="w-full h-full object-cover"
                crossOrigin="anonymous"
              />
            </div>
            <div className="absolute bottom-6 -right-2 bg-white rounded-full p-2 shadow-lg">
              <Flame className="w-8 h-8 text-rose-500" />
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-black text-gray-800 mb-2">
            You've been provoked!
          </h1>
          <p className="text-lg text-gray-500 mb-8">
            <span className="font-bold text-gray-800">{notification.fromUserName}</span> sent you a provocation.
          </p>

          <div className="bg-rose-50 rounded-2xl p-6 md:p-8 mb-8 relative">
            <div className="absolute -top-4 -left-4 text-6xl text-rose-200">"</div>
            <p className="text-2xl md:text-3xl font-bold text-rose-800 italic relative z-10 leading-relaxed">
              {notification.message}
            </p>
            <div className="absolute -bottom-8 -right-4 text-6xl text-rose-200 transform rotate-180">"</div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <button 
              onClick={handleChallengeBack}
              className="px-6 py-4 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg"
            >
              <Swords className="w-5 h-5" />
              Challenge Back
            </button>
            <button 
              onClick={handleProvokeBack}
              className="px-6 py-4 bg-rose-100 text-rose-700 rounded-xl font-bold hover:bg-rose-200 transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg"
            >
              <MessageSquare className="w-5 h-5" />
              Provoke Back
            </button>
            <button 
              onClick={handleShare}
              className="px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg"
            >
              <Share2 className="w-5 h-5" />
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Hidden element for sharing */}
      <div className="absolute -left-[9999px] top-0">
        <div ref={shareRef} className="w-[600px] h-[400px] bg-gradient-to-br from-red-600 to-rose-700 flex flex-col items-center justify-center p-8 font-sans text-white">
          <div className="flex items-center gap-3 mb-6">
            <Flame className="w-12 h-12 text-yellow-300" />
            <h1 className="text-4xl font-black tracking-tight uppercase">I've been challenged!</h1>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 w-full max-w-lg border border-white/20 shadow-2xl flex flex-col items-center relative">
             <div className="absolute -top-10 flex gap-4">
                <div className="w-20 h-20 rounded-full border-4 border-white shadow-xl overflow-hidden bg-white">
                   <img referrerPolicy="no-referrer" src={notification.fromUserPhoto || getAvatarUrl(notification.fromUserName || notification.fromUserId || 'User')} className="w-full h-full object-cover" crossOrigin="anonymous" />
                </div>
             </div>
             
             <div className="mt-12 text-center">
               <p className="text-2xl font-bold italic mb-4 leading-relaxed">"{notification.message || 'Wake up and study!'}"</p>
               <p className="text-sm text-red-200 font-medium uppercase tracking-widest">— {notification.fromUserName} poking me</p>
             </div>
          </div>
          
          <div className="mt-8 flex flex-col items-center text-center">
             <p className="text-white/80 font-bold tracking-widest uppercase text-xs mb-1">Watch me beat them</p>
             <p className="text-white font-bold text-sm bg-black/20 px-4 py-1 rounded-full backdrop-blur-sm">https://lingualearn.web.app/</p>
          </div>
        </div>
      </div>
    </div>
  );
}
